"use client"
import { useEffect, useState, useCallback } from "react"
import Cookies from "js-cookie"
import { useRouter } from "next/navigation"
import { ContentfulLivePreview } from "@contentful/live-preview"
import { getProfile } from "@/profiles/getProfile"

function extractPrefix(input:string) {
  const match2 = input.match(/^\/?([a-zA-Z]{2}(?:-[a-zA-Z]{2,4})?)(?:\/.*)?$/);
  return match2 ? match2[1] : null;
}
function removeLocaleFromString(pathname:any): any {
  const possibleLocale = extractPrefix(pathname)
  if(possibleLocale !== null) { pathname = pathname.replace(`/${possibleLocale}`, '') }
  return pathname;
}

const useLanguage = (
  location: any,
  languages: any,
  profile: any,
) => {
  const [languageSettings, setLanguageSettings] = useState({ language: null })
  const router = useRouter();
  const saveLanguageSettings = useCallback((newLanguage: any) => {
    // handles setting cookie/localstorage for language, and redirecting to the new language URL
    if (newLanguage) {
      newLanguage.language["code"] = newLanguage.language.code
      const updatedLang = JSON.stringify({ 
        language: {
          nativeField: newLanguage?.language?.nativeField,
          translatableName: newLanguage?.language?.translatableName,
          code: newLanguage?.language?.code,
        }
      })
      if (
        location?.host?.includes("create.netlify.com") ||
        location?.host?.includes("localhost:3000")
      ) {
        localStorage.setItem("rcLanguageSettings", updatedLang)
      } else {
        Cookies.set("rcLanguageSettings", updatedLang)
      }

      let path = ""
      if (location?.pathname) {
        const slashIndex = location?.pathname?.indexOf("/", 1)
        path = slashIndex !== -1 ? location?.pathname?.slice(slashIndex) : ""
      }
      const redirectLangBypass = Cookies.get("redirectLangBypass") // used by Algolia to see pages normally redirected
      if(ContentfulLivePreview?.initialized === false && redirectLangBypass !== "true"){
        const pathRemoveLang = removeLocaleFromString(path)
        const newPath = `${profile.BASE_URL}/${newLanguage?.language?.code}${pathRemoveLang}${location?.search}`
        router?.push(newPath)
      }
    }
  }, [location, router, profile.BASE_URL]);
  useEffect(() => {
    const checkValidLanguage = (langCode: string) => {
      let returnedLang
      languages?.forEach((language: any) => {
        if (language?.code === langCode) {
          const newLanguage = {
            language: language
          }
          returnedLang = newLanguage
        }
      })
      return returnedLang;
    }
    const detectLanguage = () => {
      let detectedLanguage: any;
      if (localStorage?.getItem("rcLanguageSettings")) {
        // IF LOCALSTORAGE IS SET, USE LOCALSTORAGE VALUE FOR LANG
        const localStorageLang = localStorage?.getItem("rcLanguageSettings");
        const parsedLang = localStorageLang && JSON?.parse(localStorageLang)
        detectedLanguage = checkValidLanguage(parsedLang?.language?.code);
      } else if (Cookies.get("rcLanguageSettings")){
        // IF COOKIE IS SET, USE COOKIE VALUE FOR LANG
        const cookieLang = Cookies.get("rcLanguageSettings");
        const parsedLang = cookieLang && JSON?.parse(cookieLang)
        detectedLanguage = checkValidLanguage(parsedLang?.language?.code);
      } else {
        // if cookie or localstorage isn't set, try to detect lang from the URL. 
        // If valid lang code is in URL, set it as the saved language. 
        // Otherwise, set to english as default.
        const langUrl = location?.href?.split('/')
        const detectedCode = langUrl[langUrl?.length - 1];
        detectedLanguage = checkValidLanguage(detectedCode) ? checkValidLanguage(detectedCode) : {
          language: {
          nativeField: "English (United States)",
          translatableName: "English",
          code: "en-US",
        }}
      }
      setLanguageSettings(detectedLanguage)
      saveLanguageSettings(detectedLanguage)
    } 
    detectLanguage()
  }, [languages, location?.href, location?.host, saveLanguageSettings])
  return { languageSettings, saveLanguageSettings }
}

export default useLanguage
